import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AddTransactionDialog({ accounts, categories, onCreated, trigger }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const filteredCats = categories.filter((c) => c.type === type);

  const suggestCategory = async () => {
    if (!description.trim()) {
      toast.error("Ajoutez une description");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/ai/categorize", {
        description,
        amount: amount ? parseFloat(amount) : null,
        type,
      });
      if (data.category_id) {
        setCategoryId(data.category_id);
        toast.success(`Catégorie suggérée : ${data.category_name}`);
      } else {
        toast.info(`Suggestion IA : ${data.suggestion} (non reconnue)`);
      }
    } catch (e) {
      toast.error("Erreur IA");
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async () => {
    if (!accountId || !categoryId || !amount) {
      toast.error("Remplissez tous les champs");
      return;
    }
    setSaving(true);
    try {
      await api.post("/transactions", {
        type,
        amount: parseFloat(amount),
        description,
        account_id: accountId,
        category_id: categoryId,
        date,
      });
      toast.success("Transaction ajoutée");
      setOpen(false);
      setAmount(""); setDescription(""); setCategoryId("");
      onCreated?.();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="btn-add-transaction">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nouvelle transaction</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => { setType("expense"); setCategoryId(""); }}
              data-testid="tx-type-expense"
              className={`py-3 rounded-xl border font-medium text-sm transition-colors ${
                type === "expense" ? "bg-rose-50 border-rose-500 text-rose-700" : "border-stone-200 text-stone-500"
              }`}
            >
              Dépense
            </button>
            <button
              onClick={() => { setType("income"); setCategoryId(""); }}
              data-testid="tx-type-income"
              className={`py-3 rounded-xl border font-medium text-sm transition-colors ${
                type === "income" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "border-stone-200 text-stone-500"
              }`}
            >
              Revenu
            </button>
          </div>
          <div className="space-y-1.5">
            <Label>Montant (€)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                   className="rounded-xl h-11 text-lg font-semibold" placeholder="0,00" data-testid="tx-amount-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Compte</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="rounded-xl h-11" data-testid="tx-account-select">
                <SelectValue placeholder="Choisir un compte" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Catégorie</Label>
              <button
                type="button"
                onClick={suggestCategory}
                disabled={suggesting || !description.trim()}
                data-testid="tx-ai-suggest-button"
                className="text-xs font-medium text-amber-700 hover:text-amber-800 disabled:text-stone-300 flex items-center gap-1 transition-colors"
              >
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Suggérer avec l'IA
              </button>
            </div>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="rounded-xl h-11" data-testid="tx-category-select">
                <SelectValue placeholder="Choisir une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {filteredCats.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="rounded-xl h-11" data-testid="tx-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Description (optionnel)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      className="rounded-xl" rows={2} data-testid="tx-description-input"
                      placeholder="Ex: Courses Carrefour" />
          </div>
          <Button onClick={submit} disabled={saving}
                  className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800"
                  data-testid="tx-submit-button">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
